import { useMemo, useRef, useState, useEffect } from "react";
import { Input } from "./ui/input";
import { Search, X, Check } from "lucide-react";

export const MemberSearchSelect = ({ users, value, onChange, testId, placeholder = "Search member by name or phone..." }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);

  const selected = users.find((u) => u.id === value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? users.filter((u) => (u.name || "").toLowerCase().includes(q) || (u.phone || "").includes(q))
      : users;
    return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [users, query]);

  const pick = (userId) => {
    onChange(userId);
    setQuery("");
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && filtered.length > 0) {
      e.preventDefault();
      pick(filtered[0].id);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
      <Input
        data-testid={testId}
        placeholder={placeholder}
        value={open ? query : selected ? `${selected.name}${selected.phone ? ` (${selected.phone})` : ""}` : ""}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        className="h-11 pl-9 pr-9 rounded-xl"
      />
      {(selected || query) && (
        <button
          type="button"
          data-testid={`${testId}-clear`}
          onClick={() => {
            onChange("");
            setQuery("");
            setOpen(true);
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {open && (
        <div
          data-testid={`${testId}-list`}
          className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border bg-white shadow-lg py-1"
        >
          {filtered.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-3" data-testid={`${testId}-no-results`}>
              No members found
            </p>
          )}
          {filtered.map((u) => (
            <button
              key={u.id}
              type="button"
              data-testid={`${testId}-option-${u.id}`}
              onClick={() => pick(u.id)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-100"
            >
              <span className="truncate">
                {u.name}
                {u.phone ? <span className="text-slate-400"> ({u.phone})</span> : null}
              </span>
              {value === u.id && <Check className="h-4 w-4 text-[#064E3B] shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MemberSearchSelect;
